import type { ShopifyOrderView } from "../../api/types";
import { businessDayKey } from "../../utils/businessDay";

/**
 * G-167 (OD-283) — how the office words a web order's delivery day. The day
 * itself, how far off it is and the preparation day come from the server
 * (`web-order-dates.ts`, the one rule); this file only says them.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const dayFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "short",
  day: "numeric",
  month: "short",
});
const weekdayFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "short",
  day: "numeric",
});

const utcDay = (ymd: string): Date => {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

/** "Sat 14 Nov" for a Barcelona day (YYYY-MM-DD). */
export function calendarDayLabel(ymd: string): string {
  return dayFormat.format(utcDay(ymd)).replace(",", "");
}

/** "Sat 14 Nov" — the order's delivery day, or null when it has none. */
export function dueDayText(order: Pick<ShopifyOrderView, "due">): string | null {
  return order.due ? calendarDayLabel(order.due.day) : null;
}

/** "in 24 days · prep Fri 13" — under the delivery day in "To assign". */
export function dueDistanceText(order: Pick<ShopifyOrderView, "due">, now: Date = new Date()): string | null {
  if (!order.due) return null;
  const days = Math.round((utcDay(order.due.day).getTime() - utcDay(businessDayKey(now)).getTime()) / DAY_MS);
  const prep = weekdayFormat.format(utcDay(order.due.prepDay)).replace(",", "");
  return `in ${days} day${days === 1 ? "" : "s"} · prep ${prep}`;
}

/** "Pastel bouquet L, 3 x Candle". */
export function orderLineSummary(order: Pick<ShopifyOrderView, "lines">): string {
  return order.lines
    .map((line) => (Number(line.quantity) === 1 ? line.titleRaw : `${Number(line.quantity)} x ${line.titleRaw}`))
    .join(", ");
}
